import { useState } from 'react'
import { useApi, useAsync } from '../lib/useApi.js'
import { CopyButton, ErrorBox, Empty, Loading, relative, until } from '../components/ui.js'
import { useOrgs } from '../lib/orgs.js'

/**
 * Access tokens: the credential for the CLI and for MCP clients.
 *
 * The page exists because a browser session cannot be pasted into an agent.
 * Two things it is careful about:
 *
 * - The plaintext is shown once, in a notice that says so, with a copy
 *   button. Everywhere else only the prefix appears, because that is all the
 *   API will ever return.
 * - It states which organization the token is for, prominently. A token is
 *   scoped to one, and somebody who made a token in their personal workspace
 *   and then wondered why the agent could not see the company's projects has
 *   been failed by the UI, not by the token.
 */
export default function AccessTokens() {
  const api = useApi()
  const { active } = useOrgs()
  const tokens = useAsync(() => api.listAccessTokens(), [api])

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Access tokens</h1>
          <p className="sub">
            For the CLI and for AI agents over MCP - anything without a browser to sign in with. A token acts
            as you, with your role, in <strong>{active?.name}</strong> only.
          </p>
        </div>
      </div>

      <NewToken orgName={active?.name ?? ''} onCreated={tokens.reload} />

      <h2>Your tokens in this organization</h2>
      <div className="card">
        {tokens.loading ? (
          <Loading />
        ) : tokens.error ? (
          <ErrorBox retry={tokens.reload}>{tokens.error}</ErrorBox>
        ) : !tokens.data?.tokens.length ? (
          <Empty>No tokens yet. Create one above to connect the CLI or an MCP client.</Empty>
        ) : (
          tokens.data.tokens.map((t) => (
            <div className="row" key={t.id}>
              <div className="row-main">
                <div className="row-title">{t.name}</div>
                <div className="row-sub">
                  <code>{t.tokenPrefix}…</code> · created {relative(t.createdAt)}
                  {' · '}
                  {t.lastUsedAt ? `last used ${relative(t.lastUsedAt)}` : 'never used'}
                  {t.expiresAt ? ` · expires ${until(t.expiresAt)}` : ''}
                </div>
              </div>
              <RevokeButton id={t.id} name={t.name} onRevoked={tokens.reload} />
            </div>
          ))
        )}
      </div>

      <ConnectingGuide />
    </>
  )
}

function NewToken({ orgName, onCreated }: { orgName: string; onCreated: () => void }) {
  const api = useApi()
  const [name, setName] = useState('')
  const [expiry, setExpiry] = useState('90')
  const [readOnly, setReadOnly] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<{ name: string; plaintext: string } | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await api.createAccessToken({
        name: name.trim(),
        // 'never' is offered but not the default: a token that outlives the
        // laptop it was made on is the one nobody remembers to revoke.
        ...(expiry === 'never' ? {} : { expiresInDays: Number(expiry) }),
        ...(readOnly ? { scope: 'read_only' as const } : {}),
      })
      setIssued({ name: result.token.name, plaintext: result.token.plaintext })
      setName('')
      onCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create that token.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <h2>New token</h2>

      {issued && (
        <div className="notice">
          <strong>
            Token for {issued.name} — copy it now. It is stored hashed and will never be shown again.
          </strong>
          <div className="secret">{issued.plaintext}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <CopyButton value={issued.plaintext} />
            <button className="btn" onClick={() => setIssued(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <form className="card" onSubmit={submit} style={{ padding: 16, marginBottom: 16 }}>
        {error && <ErrorBox>{error}</ErrorBox>}
        <div className="form-row">
          <div className="field" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="t-name">What is it for?</label>
            <input
              id="t-name"
              value={name}
              required
              maxLength={100}
              placeholder="Claude on my laptop"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="field" style={{ width: 180, marginBottom: 0 }}>
            <label htmlFor="t-expiry">Expires</label>
            <select id="t-expiry" value={expiry} onChange={(e) => setExpiry(e.target.value)}>
              <option value="30">in 30 days</option>
              <option value="90">in 90 days</option>
              <option value="365">in a year</option>
              <option value="never">never</option>
            </select>
          </div>
          <button className="btn btn-primary" disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create token'}
          </button>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12 }}>
          <input
            type="checkbox"
            checked={readOnly}
            onChange={(e) => setReadOnly(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            <strong>Read-only</strong>
            <span className="sub small" style={{ display: 'block' }}>
              The agent can look at your schema, rows, logs and policies, and cannot change anything — no
              writes, no migrations, no keys, no deploys. This is the one to use when you are not sure what an
              agent will do.
            </span>
          </span>
        </label>
        <p className="sub small" style={{ marginTop: 10 }}>
          Scoped to <strong>{orgName}</strong>, with whatever role you have there. It stops working the moment
          you leave the organization, and your role is read fresh on every call - so a change to your
          permissions is checked on subsequent requests, without issuing a new token.
        </p>
      </form>
    </>
  )
}

function RevokeButton({ id, name, onRevoked }: { id: string; name: string; onRevoked: () => void }) {
  const api = useApi()
  const [busy, setBusy] = useState(false)
  return (
    <button
      className="btn btn-danger"
      disabled={busy}
      onClick={async () => {
        if (!confirm(`Revoke "${name}"? New requests using it will be refused after propagation.`)) return
        setBusy(true)
        try {
          await api.revokeAccessToken(id)
        } finally {
          setBusy(false)
          onRevoked()
        }
      }}
    >
      {busy ? 'Revoking…' : 'Revoke'}
    </button>
  )
}

/**
 * Where to go next.
 *
 * The per-client instructions live on the Connect agent page and are not
 * repeated here: two copies of a config block is two chances for one of them
 * to go stale, and the stale one is always the one somebody pastes.
 */
function ConnectingGuide() {
  return (
    <>
      <h2>Using a token</h2>
      <div className="card" style={{ padding: 16 }}>
        <p className="sub" style={{ marginTop: 0 }}>
          For an AI agent, <a href="/connect">Connect agent</a> has the exact command or config block for
          Claude Code, Cursor, VS Code, Windsurf, Codex and the rest - and will create the token for you.
        </p>
        <div className="secret">npx nimblybase login</div>
        <p className="sub small" style={{ marginBottom: 0 }}>
          For the CLI, paste a token above when it asks. It is stored in your keychain, or in a 0600 file
          where there is no keychain - the CLI says which. In CI, set
          <code> NIMBLYBASE_TOKEN</code> instead and skip the login.
        </p>
      </div>
    </>
  )
}
